import { Space } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, TableOutlined } from '@ant-design/icons';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import Button from 'components/new-ui/base/button/Button';
import useBreadcrumb from 'hooks/useBreadcrumb';
import useCommodityConditions from 'hooks/useCommodityConditions';
import { CommodityCondition, CommodityConditionFilters } from 'store/commodity-conditions-center/commodityCondition';
import { getTableChangeParams, useHandleTableChange } from 'utils/tableHelpers';
import DeleteCommodityConditionModal from './components/DeleteCommodityConditionModal';
import Filters from './components/Filters';
import CommodityConditionsTable from './components/CommodityConditionsTable';
import useCommodityConditionCodelists from './hooks/useCommodityConditionCodelists';
import { commodityConditionPath } from './utils/commodityConditionPath';

type Sort = ReturnType<typeof getTableChangeParams>;

const FILTER_KEYS = ['commodityCode', 'taricCode', 'countryOrigin', 'conditionCode'] as const;

// Note: This feature currently covers only AIS specific logic - we store the conditions only for AIS released declarations
const CommodityConditionsCenterPage = () => {
    const navigate = useNavigate();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { page, pageSize, onTableChange, filters, setFilter } = useHandleTableChange({ filterKeys: FILTER_KEYS });
    const [sort, setSort] = useState<Sort>({});
    const [conditionToDelete, setConditionToDelete] = useState<CommodityCondition | null>(null);
    const { conditions, isLoading, listConditions, deleteCondition } = useCommodityConditions();
    const { countryOptions, conditionCodeOptions, isLoading: isLoadingCodelists } = useCommodityConditionCodelists();

    const commodityCode: string | undefined = filters?.commodityCode || undefined;
    const taricCode: string | undefined = filters?.taricCode || undefined;
    const countryOrigin: string | undefined = filters?.countryOrigin || undefined;
    const conditionCode: string | undefined = filters?.conditionCode || undefined;
    const { sortParameter, sortDirection } = sort;

    const reloadConditions = useCallback(
        () =>
            listConditions({
                pageNumber: page,
                pageSize,
                commodityCode,
                taricCode,
                countryOrigin,
                conditionCode,
                sort: sortParameter,
                sortDirection,
            }),
        [
            listConditions,
            page,
            pageSize,
            commodityCode,
            taricCode,
            countryOrigin,
            conditionCode,
            sortParameter,
            sortDirection,
        ]
    );

    useEffect(() => {
        reloadConditions();
    }, [reloadConditions]);

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Home',
                path: 'home',
            },
            {
                breadcrumbName: 'Commodity Conditions Center',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    const handleFilterChange = (filter: keyof CommodityConditionFilters, value: string) => {
        setFilter(filter, value);
        onTableChange({ current: 1, pageSize });
    };

    const handleConfirmDelete = async () => {
        if (!conditionToDelete) return;

        const condition = conditionToDelete;
        setConditionToDelete(null);

        await deleteCondition(condition);
        reloadConditions();
    };

    return (
        <>
            <Space direction="vertical" style={{ width: '100%' }} size={24}>
                <PageHeader
                    title="Commodity Conditions Center"
                    icon={<TableOutlined />}
                    sidebar={
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate(commodityConditionPath())}
                        >
                            New Condition
                        </Button>
                    }
                />

                <Filters
                    values={{ commodityCode, taricCode, countryOrigin, conditionCode }}
                    countryOptions={countryOptions}
                    conditionCodeOptions={conditionCodeOptions}
                    isLoadingCodelists={isLoadingCodelists}
                    onChange={handleFilterChange}
                />

                <CommodityConditionsTable
                    conditions={conditions?.list}
                    loading={isLoading}
                    pagination={{
                        current: page + 1,
                        pageSize,
                        total: conditions?.total,
                        position: ['bottomCenter'],
                        showSizeChanger: true,
                        hideOnSinglePage: true,
                    }}
                    onChange={(pagination, _filters, sorter) => {
                        setSort(getTableChangeParams({ pagination, sorter }));
                        onTableChange(pagination);
                    }}
                    onOpen={(condition) => navigate(commodityConditionPath(condition.id))}
                    onDelete={setConditionToDelete}
                />
            </Space>

            <DeleteCommodityConditionModal
                condition={conditionToDelete}
                onCancel={() => setConditionToDelete(null)}
                onConfirm={handleConfirmDelete}
            />
        </>
    );
};

export default CommodityConditionsCenterPage;
